import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import { PAGE_SIZE_CHOICES } from '@/lib/pageSize';
import { Button, Select, cx } from './ui';

export interface Column<T> {
  key: string;
  header: string;
  /** Omitted where the API cannot order by it. */
  sortKey?: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort?: string;
  order?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  caption: string;
}

/**
 * One sortable table for the whole dashboard.
 *
 * Row 93 asks for "voll sortierbar", so sorting is the table's job rather than
 * something each screen reimplements. `aria-sort` is set on the active column
 * because that is the only way the sort state reaches a screen reader -- an
 * arrow glyph does not.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sort,
  order,
  onSort,
  caption,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-line">
            {columns.map((column) => {
              const active = column.sortKey && column.sortKey === sort;
              const ariaSort = active
                ? order === 'asc'
                  ? 'ascending'
                  : 'descending'
                : undefined;
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={cx('th', column.align === 'right' && 'text-right')}
                >
                  {column.sortKey && onSort ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.sortKey as string)}
                      // The label says what pressing it will do, not what the
                      // state currently is -- the state is on aria-sort.
                      aria-label={`${column.header}: ${
                        active && order === 'asc'
                          ? t('common.sortDescending')
                          : t('common.sortAscending')
                      }`}
                      className={cx(
                        'inline-flex items-center gap-1 rounded transition-colors hover:text-ink',
                        column.align === 'right' && 'flex-row-reverse',
                        active && 'text-ink',
                      )}
                    >
                      {column.header}
                      {active ? (
                        order === 'asc' ? (
                          <ArrowUp aria-hidden className="h-3 w-3" />
                        ) : (
                          <ArrowDown aria-hidden className="h-3 w-3" />
                        )
                      ) : (
                        <ChevronsUpDown aria-hidden className="h-3 w-3 opacity-40" />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="transition-colors hover:bg-surface-sunken/60"
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cx('td', column.align === 'right' && 'text-right tabular')}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The page-length picker every list shares. Sits in the filter row; the
 * caller owns the state (usePageSize) and resets its page to 1 on change.
 */
export function PageSizeSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <Select
      aria-label={t('common.perPageLabel')}
      value={String(value)}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {PAGE_SIZE_CHOICES.map((choice) => (
        <option key={choice} value={choice}>
          {t('common.perPage', { n: choice })}
        </option>
      ))}
    </Select>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-3 py-2.5">
      <p className="text-xs text-ink-subtle tabular">
        {t('common.showing', { from, to, total })}
      </p>
      <div className="flex gap-2">
        <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {t('common.previous')}
        </Button>
        <Button disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}
